import {
	BUDGET_TYPE,
	type BudgetType,
	HIDDEN_FEATURES,
	MODULE_TYPES,
	PERIOD_BUDGET_TYPE,
	type PeriodBudgetType,
} from '../../../constants';
import CompanySetupUtil, {isFeatureHidden} from './CompanySetupUtil';
import {hasFeatureFlag} from './FeatureUtil';
import {hasModule} from './ModuleUtil';
import {Connection, ConnectionEdge} from './type-util/Connection';

export type ProjectNode = Partial<{
	totalBillableExpensesAtCompletion: number | null;
	totalTimeRevenueRecognitionToDate: number | null;
	totalRevenueRecognition: number | null;
	manualProgressOnProjectEnabled: boolean | null;
	manualProgressOnPhasesEnabled: boolean | null;
	manualProgressOnTasksEnabled: boolean | null;
	useBaseline: boolean | null;
	budgetType: string | null;
	defaultPeriodBudgetType: PeriodBudgetType;
}>;
type ProjectEdge = ConnectionEdge<ProjectNode>;

type Task = {
	totalCostAtCompletion: number | null;
	estimateForecastPrice: number | null;
	allTotalTimeAndExpensesAtCompletion: number | null;
	totalPriceAtCompletion: number | null;
	actualCost: number | null;
	plannedCost: number | null;
	currentPrice: number;
	approved: unknown;
	timeRegistrations: Connection<{price: number}, null> | null;
	billable: unknown;
};

export default class ProjectUtil {
	static handleSprintLengthChange(value: string): string | number {
		// Clamp sprint length between 0 and 365
		const clampedValue = Math.max(0, Math.min(365, parseInt(value, 10)));
		return isNaN(clampedValue) ? value : clampedValue;
	}

	static getDefaultRateCard(
		formatMessage: (arg: unknown) => string,
		defaultRateCard: {id: unknown; name: unknown; currency: unknown} | undefined,
		companyCurrency: unknown
	): {value: unknown; label: unknown; currency: unknown} {
		if (defaultRateCard) {
			return {
				value: defaultRateCard.id,
				label: defaultRateCard.name,
				currency: defaultRateCard ? defaultRateCard.currency : companyCurrency,
			};
		} else {
			return {value: null, label: formatMessage({id: 'rate_card_selector.no_rate_card_text'}), currency: companyCurrency};
		}
	}

	static projectHasManualProgressSettingEnabled(project: ProjectNode | null): boolean | null | undefined {
		return (
			project &&
			(project.manualProgressOnProjectEnabled ||
				project.manualProgressOnPhasesEnabled ||
				project.manualProgressOnTasksEnabled)
		);
	}

	static projectUsesManualProgress(project: ProjectEdge | ProjectNode | null): boolean | null | undefined {
		const projectEntity = (project as ProjectEdge | null)?.node || (project as ProjectNode | null);
		return (
			(isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) && !hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED)) ||
			this.projectHasManualProgressSettingEnabled(projectEntity)
		);
	}

	static projectTracksTime(project: ProjectEdge | ProjectNode | null): boolean | null {
		const projectEntity = (project as ProjectEdge | null)?.node || (project as ProjectNode | null);
		return (
			!isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) && !this.projectHasManualProgressSettingEnabled(projectEntity)
		);
	}

	static projectTracksRevenue(project: Pick<ProjectNode, 'budgetType'> | null): boolean {
		return !isFeatureHidden(HIDDEN_FEATURES.REVENUE) && project?.budgetType !== BUDGET_TYPE.NON_BILLABLE;
	}

	static projectTracksCost(): boolean {
		return CompanySetupUtil.hasCost();
	}

	static projectHasTaskBreakdown(budgetType: BudgetType): boolean {
		if (hasModule(MODULE_TYPES.SAGE_PSA_RESTRICTED) && !isFeatureHidden(HIDDEN_FEATURES.TASK_BREAKDOWN)) {
			return true;
		}

		return this.projectHasRevenueRecognition(budgetType);
	}

	static projectHasRevenueRecognition(budgetType: BudgetType): boolean {
		return CompanySetupUtil.hasRevenueRecognition() && ProjectUtil.projectTracksRevenue({budgetType});
	}

	static projectHasExpenses(budgetType: BudgetType): boolean {
		return ProjectUtil.projectTracksRevenue({budgetType}) && CompanySetupUtil.hasExpenses();
	}

	static projectHasInvoicing(budgetType: BudgetType): boolean {
		return ProjectUtil.projectTracksRevenue({budgetType}) && CompanySetupUtil.hasFinance();
	}

	static isFixedPriceV1(project: ProjectNode): boolean {
		return ProjectUtil.projectTracksRevenue(project) && project.budgetType === BUDGET_TYPE.FIXED_PRICE;
	}

	static isFixedPriceV2(project: ProjectNode): boolean {
		return ProjectUtil.projectTracksRevenue(project) && project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	}

	static isAnyKindOfFixedPrice(project: ProjectNode): boolean {
		return (
			ProjectUtil.projectTracksRevenue(project) &&
			(project.budgetType === BUDGET_TYPE.FIXED_PRICE ||
				project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 ||
				(project.budgetType === BUDGET_TYPE.RETAINER &&
					project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE))
		);
	}

	static allProjectsUseManualProgress = (projects: ProjectNode[] | ProjectEdge[] | null): boolean => {
		if (isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS)) {
			return true;
		}
		if (!projects || !Array.isArray(projects)) {
			return false;
		}
		return (projects as Array<ProjectNode | ProjectEdge>).every((project: ProjectNode | ProjectEdge) =>
			ProjectUtil.projectUsesManualProgress(project)
		);
	};

	static allProjectEdgesUseManualProgress = (edges: ProjectEdge[] | null): boolean => {
		if (isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS)) {
			return true;
		}
		return edges ? ProjectUtil.allProjectsUseManualProgress(edges.map(edge => edge.node)) : false;
	};

	/**
	 * returns correct actual revenue based on billable
	 * @param task task
	 * - taskCount: amount of tasks in project
	 * - billableScope: combined scope of all billable + approved tasks in minutes
	 * - newFixedPrice: project fixed revenue if has featureflag and fixed revenue project, undefined otherwise
	 * @returns actual revenue for task
	 */
	static getActualPriceForTask(task: Task): number {
		if (hasFeatureFlag('use_financial_service_in_scoping')) {
			return task.currentPrice;
		}
		return task.billable && task.approved && task.timeRegistrations?.edges && task.timeRegistrations.edges.length > 0
			? task.timeRegistrations.edges.reduce((acc, timeReg) => acc + timeReg.node.price, 0)
			: 0;
	}

	static getPlannedCostForTask(task: Task): number {
		return hasFeatureFlag('use_financial_service_in_scoping') ? task.plannedCost || 0 : 0;
	}

	static getActualCostForTask(task: Task): number {
		return hasFeatureFlag('use_financial_service_in_scoping') ? task.actualCost || 0 : 0;
	}

	static getTotalPriceAtCompletionForTask(task: Task): number {
		return task.totalPriceAtCompletion || 0;
	}

	static getTotalAllTotalTimeAndExpensesAtCompletionForTask(task: Task): number {
		return task.allTotalTimeAndExpensesAtCompletion || 0;
	}

	/**
	 * returns correct Planned Revenue based on billable
	 * @param task task
	 * - taskCount: amount of tasks in project
	 * - billableScope: combined scope of all billable + approved tasks in minutes
	 * - newFixedPrice: project fixed revenue if has featureflag and fixed revenue project, undefined otherwise
	 * @returns Planned Revenue for task
	 */
	static getEstimatedPrice(task: Task): number | null {
		if (hasFeatureFlag('use_financial_service_in_scoping')) {
			return task.estimateForecastPrice;
		}
		return task.billable && task.approved && task.estimateForecastPrice !== null ? task.estimateForecastPrice : 0;
	}

	static isFixedPriceProject(project: ProjectNode | null): boolean {
		return (
			project?.budgetType === BUDGET_TYPE.FIXED_PRICE ||
			project?.budgetType === BUDGET_TYPE.FIXED_PRICE_V2 ||
			(project?.budgetType === BUDGET_TYPE.RETAINER &&
				project?.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE)
		);
	}

	static getProjectConfirmedRevenue(project: ProjectNode): number {
		return ProjectUtil.isFixedPriceProject(project)
			? project.totalRevenueRecognition || 0
			: (project.totalTimeRevenueRecognitionToDate || 0) + (project.totalBillableExpensesAtCompletion || 0);
	}

	static getProjectConfirmedRevenueTotal = (projects: ProjectNode[] | null): number => {
		if (!projects || !projects.reduce) {
			return 0;
		}
		return projects.reduce((total, project) => {
			const projectConfirmedRevenue = ProjectUtil.getProjectConfirmedRevenue(project);
			return total + projectConfirmedRevenue;
		}, 0);
	};

	static getProjectedBillableValueOfServiceForTask(task: Task): number | null {
		if (hasFeatureFlag('use_financial_service_in_scoping')) {
			return task.totalPriceAtCompletion;
		}
		return task.billable && task.approved && task.totalPriceAtCompletion !== null ? task.totalPriceAtCompletion : 0;
	}

	static getProjectedCostForTask(task: Task): number | null {
		if (hasFeatureFlag('use_financial_service_in_scoping')) {
			return task.totalCostAtCompletion;
		}
		return task.billable && task.approved && task.totalCostAtCompletion !== null ? task.totalCostAtCompletion : 0;
	}

	static projectCompatibleWithDeliverables(project: ProjectNode): boolean | null | undefined {
		const compatibleTypes: string[] = [BUDGET_TYPE.FIXED_PRICE, BUDGET_TYPE.FIXED_PRICE_V2, BUDGET_TYPE.TIME_AND_MATERIALS];
		return !!(project.budgetType && compatibleTypes.includes(project.budgetType) && project.useBaseline);
	}
}
